import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import { VatDictionaryService } from '../../../accounting/accounting-settings-module/services';
import { selectCompanyProfile } from '../../../administration/store/selectors';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { AppState } from '../../../store/state/app.state';
import { AddVatRateModalComponent } from 'common/src/modules/modals/modals-common';
import { VatDictionaryItemModel } from '../../../accounting/accounting-settings-module/models';
import { formatVatRateLabel } from 'common/src/modules/rnpl-common/helpers';
import { GeneralPartnerTypeEnum } from 'common/src/models';
import { VatRatesFlowEnum } from '../../enums';

@Component({
  selector: 'rnpl-vat-rates-dropdown',
  templateUrl: './vat-rates-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VatRatesDropdownComponent implements OnInit, OnDestroy, OnChanges {

  @Input() vatControl: FormControl = new FormControl(null, [Validators.required]);
  @Input() validate: false;
  @Input() vatControlValue: number = null;
  @Input() vatControlDisabled: boolean = false;
  @Input() label: string = 'FORM.VAT';
  @Input() vatBlocked: boolean = false;
  @Input() iso3ForVat: string;
  @Input() generalPartnerType: GeneralPartnerTypeEnum;
  @Input() ocrCountryIso3: string;
  @Input() flow: VatRatesFlowEnum = VatRatesFlowEnum.OUTGOING;
  @Input() preserveScrollOnOpen: boolean = true;
  @Input() includeCurrentValue: boolean = true;

  @Output() fieldChanged: EventEmitter<number> = new EventEmitter<number>();

  public vatList: {value: number; label: string}[] = [];
  public hideSelectedVatRate: boolean = false;
  public legalCountryIso3: string = null;
  private companyProfile: CompanyProfile;
  private vatControlSubscription: Subscription;
  private ratesSubscription: Subscription;
  private defaultVatRateLoading: boolean = false;
  private dictionaryRates: number[] = [];

  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);

  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private vatDictionaryService: VatDictionaryService
  ) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.hasOwnProperty('vatControlValue')) {
      this.vatControl.patchValue(this.vatControlValue);
      this.updateVatList();
    }
    if (changes && changes.hasOwnProperty('includeCurrentValue')) {
      this.updateVatList();
    }
    if (changes && changes.hasOwnProperty('vatControlDisabled')) {
      this.vatControlDisabled
        ? this.vatControl.disable()
        : this.vatControl.enable();
    }
    if (changes && changes.hasOwnProperty('vatControl') && this.companyProfile) {
      this.trackVatControlChanges();
      this.setDefaultVatRate();
    }
    if (this.companyProfile && (
      changes.hasOwnProperty('iso3ForVat')
      || changes.hasOwnProperty('generalPartnerType')
      || changes.hasOwnProperty('ocrCountryIso3')
      || changes.hasOwnProperty('flow')
    )) {
      this.getRates();
    }
  }

  ngOnInit(): void {
    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
        this.legalCountryIso3 = companyProfile.legalAddress.country_iso3;
        this.trackVatControlChanges();
        this.getRates();
      });
  }

  private trackVatControlChanges(): void {
    if (this.vatControlSubscription) {
      this.vatControlSubscription.unsubscribe();
    }

    this.vatControlSubscription = this.vatControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateVatList();
        if (!this.hasVatValue() && this.companyProfile) {
          this.setDefaultVatRate();
        }
      });
  }

  public getRates(): void {
    const partnerCountry = this.getPartnerCountryFilter();
    const incomingGeneralForeignPartner = this.isIncomingGeneralForeignPartner();
    let countryFilters: string[];

    if (incomingGeneralForeignPartner) {
      countryFilters = [partnerCountry];
    } else if (this.flow === VatRatesFlowEnum.INCOMING) {
      countryFilters = [partnerCountry, this.legalCountryIso3];
    } else {
      countryFilters = [this.legalCountryIso3, partnerCountry];
    }

    const countriesIso3 = this.uniqueCountries(countryFilters);

    if (!countriesIso3.length) { return; }

    if (this.ratesSubscription) {
      this.ratesSubscription.unsubscribe();
    }

    this.ratesSubscription = this.vatDictionaryService.getVatDictionaryRates(countriesIso3, this.flow)
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        this.dictionaryRates = list;
        this.updateVatList();
        this.setDefaultVatRate();
        this.cdr.detectChanges();
      });
  }

  private getPartnerCountryFilter(): string {
    if (this.flow === VatRatesFlowEnum.INCOMING) {
      if (this.isIncomingGeneralForeignPartner() && this.ocrCountryIso3) {
        return this.normalizeCountryFilter(this.ocrCountryIso3);
      }

      if (this.generalPartnerType === GeneralPartnerTypeEnum.EU) {
        return 'EU';
      }

      if (this.generalPartnerType === GeneralPartnerTypeEnum.NON_EU) {
        return 'NON_EU';
      }

      if (this.generalPartnerType === GeneralPartnerTypeEnum.INLAND) {
        return null;
      }
    }

    return this.normalizeCountryFilter(this.iso3ForVat);
  }

  private isIncomingGeneralForeignPartner(): boolean {
    return this.flow === VatRatesFlowEnum.INCOMING && (
      this.generalPartnerType === GeneralPartnerTypeEnum.EU
      || this.generalPartnerType === GeneralPartnerTypeEnum.NON_EU
    );
  }

  private uniqueCountries(countries: string[]): string[] {
    return countries
      .filter(country => !!country)
      .map(country => this.normalizeCountryFilter(country))
      .filter((country, index, list) => list.indexOf(country) === index);
  }

  private normalizeCountryFilter(country: string): string {
    if (!country) { return null; }

    const normalized = country.toUpperCase();
    return normalized === 'EU' || normalized === 'NON_EU'
      ? normalized
      : country.toLowerCase();
  }

  private updateVatList(): void {
    const rates = [...this.dictionaryRates];
    const controlValue = this.vatControl && this.vatControl.value;
    const currentRate = typeof controlValue === 'string'
      ? Number(controlValue.replace(',', '.'))
      : Number(controlValue);
    const hasCurrentRate = controlValue !== null && controlValue !== undefined && controlValue !== '' && !isNaN(currentRate);
    const currentRateExists = rates.some(rate => +rate === currentRate);

    this.hideSelectedVatRate = this.includeCurrentValue && hasCurrentRate && !currentRateExists;

    if (this.hideSelectedVatRate) {
      const zeroRateIndex = rates.findIndex(rate => +rate === 0);
      rates.splice(zeroRateIndex < 0 ? rates.length : zeroRateIndex, 0, currentRate);
    }

    this.vatList = rates.map(i => ({
      value: i,
      label: formatVatRateLabel(i)
    }));
  }

  private setDefaultVatRate(): void {
    if (this.hasVatValue() || this.defaultVatRateLoading) { return; }

    if (this.companyProfile.isSmallBusiness) {
      this.vatControl.patchValue(0);
      return;
    }

    this.defaultVatRateLoading = true;
    this.vatDictionaryService.getVatDictionary([this.legalCountryIso3])
      .pipe(
        finalize(() => this.defaultVatRateLoading = false),
        takeUntil(this.destroy$)
      )
      .subscribe((items: VatDictionaryItemModel[]) => {
        if (this.hasVatValue()) { return; }

        const primaryRate = items.find(item => item.primary);
        if (primaryRate) {
          this.vatControl.patchValue(primaryRate.rate);
          this.cdr.detectChanges();
        }
      });
  }

  private hasVatValue(): boolean {
    const value = this.vatControl.value;
    return value !== null && value !== undefined && value !== '';
  }

  public updateField(fieldValue: number): void {
    this.fieldChanged.emit(fieldValue);
  }

  public addRate(): void {
    const partnerCountry = this.getPartnerCountryFilter();
    const iso3ForVat = partnerCountry === 'EU' || partnerCountry === 'NON_EU'
      ? null
      : partnerCountry;

    this.dialog.open(AddVatRateModalComponent, {
      data: {
        countryIso3: this.legalCountryIso3,
        iso3ForVat
      },
      backdropClass: 'dragged-modal-type',
      disableClose: true,
      minWidth: `${336}px`,
      maxWidth: `${336}px`,
      panelClass: ['dragged-panel', 'sm-modal'],
      position: {
      }
    }).afterClosed().subscribe((result: VatDictionaryItemModel) => {
      if (result) {
        this.vatControl.patchValue(result.rate);
        this.updateField(result.rate);
        this.getRates();
      }
    });
  }

  ngOnDestroy() {
    if (this.vatControlSubscription) {
      this.vatControlSubscription.unsubscribe();
    }
    if (this.ratesSubscription) {
      this.ratesSubscription.unsubscribe();
    }
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
